import React, { useState, useEffect, useCallback } from "react";
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  Button,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  Notifications as NotificationsIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { useHistory } from "react-router-dom";
import { alpha } from "@mui/material/styles";
import useApp from "../../store/contexts/AppContext";
import { ROUTES } from "../../routes/User/paths";
import SOCKET_EVENTS from "../../config/SocketEvents";

const NotificationBell = () => {
  const history = useHistory();
  const {
    getUserNotificationsApiCall,
    getUnreadNotificationCountApiCall,
    markNotificationAsReadApiCall,
    markAllNotificationsAsReadApiCall,
    appState: { socket },
  } = useApp();

  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const open = Boolean(anchorEl);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    const result = await getUnreadNotificationCountApiCall();
    if (result.success) {
      setUnreadCount(result.count);
    }
  }, [getUnreadNotificationCountApiCall]);

  // Fetch notifications list
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    const result = await getUserNotificationsApiCall({ pageSize: 10 });
    if (result.success) {
      setNotifications(result.data.notifications);
    }
    setLoading(false);
  }, [getUserNotificationsApiCall]);

  // Initial fetch only - socket will handle updates in real-time
  useEffect(() => {
    fetchUnreadCount();
    // No polling - rely on socket events for real-time updates
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Socket event handlers
  useEffect(() => {
    if (!socket) return;

    const handleUnreadCountUpdate = ({ increment }) => {
      setUnreadCount((prev) => Math.max(0, prev + increment));
    };

    const handleNewNotification = (newNotification) => {
      setUnreadCount((prev) => prev + 1);
      // Only fetch notifications if menu is open
      if (anchorEl) {
        setLoading(true);
        getUserNotificationsApiCall({ pageSize: 10 }).then((result) => {
          if (result.success) {
            setNotifications(result.data.notifications);
          }
          setLoading(false);
        });
      }
    };

    const handleNotificationDeleted = ({ notification_id }) => {
      // Remove from local state immediately
      setNotifications((prev) =>
        prev.filter((n) => n.notification_id !== notification_id),
      );

      // Update unread count optimistically
      setUnreadCount((prev) => Math.max(0, prev - 1));
    };

    socket.on(
      SOCKET_EVENTS.UNREAD_NOTIFICATION_COUNT_UPDATE,
      handleUnreadCountUpdate,
    );
    socket.on(SOCKET_EVENTS.NEW_NOTIFICATION_BROADCAST, handleNewNotification);
    socket.on(
      SOCKET_EVENTS.NOTIFICATION_DELETED_BROADCAST,
      handleNotificationDeleted,
    );

    return () => {
      socket.off(
        SOCKET_EVENTS.UNREAD_NOTIFICATION_COUNT_UPDATE,
        handleUnreadCountUpdate,
      );
      socket.off(
        SOCKET_EVENTS.NEW_NOTIFICATION_BROADCAST,
        handleNewNotification,
      );
      socket.off(
        SOCKET_EVENTS.NOTIFICATION_DELETED_BROADCAST,
        handleNotificationDeleted,
      );
    };
  }, [socket, anchorEl, getUserNotificationsApiCall]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    fetchNotifications();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAsRead = async (notificationId, isRead) => {
    if (isRead) return;

    const result = await markNotificationAsReadApiCall(notificationId);
    if (result.success) {
      setNotifications((prev) =>
        prev.map((n) =>
          n.notification_id === notificationId ? { ...n, is_read: true } : n,
        ),
      );
      fetchUnreadCount();
    }
  };

  const handleMarkAllAsRead = async () => {
    const result = await markAllNotificationsAsReadApiCall();
    if (result.success) {
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    }
  };

  const handleViewAll = () => {
    handleClose();
    history.push(ROUTES.notifications);
  };

  const getNotificationIcon = (type) => {
    const iconProps = { fontSize: "small" };
    switch (type) {
      case "info":
        return <InfoIcon {...iconProps} color="info" />;
      case "warning":
        return <WarningIcon {...iconProps} color="warning" />;
      case "success":
        return <SuccessIcon {...iconProps} color="success" />;
      case "error":
        return <ErrorIcon {...iconProps} color="error" />;
      default:
        return <InfoIcon {...iconProps} />;
    }
  };

  const getPriorityChip = (priority) => {
    const colors = {
      high: "error",
      medium: "warning",
      low: "default",
    };
    return (
      <Chip
        label={priority.toUpperCase()}
        size="small"
        color={colors[priority] || "default"}
        sx={{ ml: 1, height: 20, fontSize: "0.65rem", fontWeight: 700 }}
      />
    );
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        aria-label="notifications"
        sx={(theme) => ({
          color: theme.palette.mode === "dark" ? "#FFFFFF !important" : "#2a3478 !important",
          transition: "all 0.3s ease",
          opacity: 1,
          filter: theme.palette.mode === "dark" ? "brightness(1.2)" : "none",
          "&:hover": {
            backgroundColor: theme.palette.mode === "dark"
              ? alpha("#FFFFFF", 0.2)
              : alpha(theme.palette.primary.main, 0.15),
            transform: "scale(1.15)",
            filter: theme.palette.mode === "dark" ? "brightness(1.5)" : "none",
            boxShadow: theme.palette.mode === "dark"
              ? "0 0 8px rgba(255, 255, 255, 0.5)"
              : `0 0 8px ${alpha(theme.palette.primary.main, 0.3)}`,
          },
          "& svg": {
            color: theme.palette.mode === "dark" ? "#FFFFFF !important" : "#2a3478 !important",
            fill: theme.palette.mode === "dark" ? "#FFFFFF !important" : "#2a3478 !important",
          },
        })}
      >
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: (theme) => ({
            width: 380,
            maxHeight: 520,
            backgroundColor:
              theme.palette.mode === "dark"
                ? alpha(theme.palette.background.paper, 0.9)
                : alpha(theme.palette.background.paper, 0.95),
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            border: `1px solid ${
              theme.palette.mode === "dark"
                ? alpha(theme.palette.common.white, 0.1)
                : alpha(theme.palette.common.black, 0.1)
            }`,
            borderRadius: 2,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 8px 32px rgba(0, 0, 0, 0.4)"
                : "0 8px 32px rgba(0, 0, 0, 0.15)",
          }),
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Box sx={{ p: 2, pb: 1 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Notifications
            </Typography>
            {unreadCount > 0 && (
              <Button
                size="small"
                onClick={handleMarkAllAsRead}
                sx={{
                  color: "#d44401",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  "&:hover": {
                    backgroundColor: alpha("#d44401", 0.1),
                  },
                }}
              >
                Mark all read
              </Button>
            )}
          </Box>
        </Box>
        <Divider sx={{ borderColor: alpha("#d44401", 0.3) }} />

        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress size={30} sx={{ color: "#d44401" }} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box p={3} textAlign="center">
            <Typography variant="body2" color="text.secondary">
              No notifications
            </Typography>
          </Box>
        ) : (
          <>
            {notifications.slice(0, 5).map((notification) => (
              <MenuItem
                key={notification.user_notification_id}
                onClick={() =>
                  handleMarkAsRead(
                    notification.notification_id,
                    notification.is_read,
                  )
                }
                sx={(theme) => ({
                  backgroundColor: notification.is_read
                    ? "transparent"
                    : alpha(theme.palette.primary.main, 0.08),
                  borderLeft: notification.is_read
                    ? "none"
                    : `3px solid ${theme.palette.primary.main}`,
                  whiteSpace: "normal",
                  display: "block",
                  py: 1.5,
                  px: 2,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? alpha(theme.palette.common.white, 0.05)
                        : alpha(theme.palette.primary.main, 0.05),
                  },
                })}
              >
                <Box display="flex" alignItems="flex-start" gap={1.5}>
                  {getNotificationIcon(notification.type)}
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" flexWrap="wrap">
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: "text.primary",
                          fontSize: "0.875rem",
                        }}
                      >
                        {notification.title}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        mt: 0.5,
                        fontSize: "0.8rem",
                        lineHeight: 1.4,
                      }}
                    >
                      {notification.message}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.disabled",
                        mt: 0.5,
                        display: "block",
                        fontSize: "0.7rem",
                      }}
                    >
                      {new Date(
                        notification.notification_created_at,
                      ).toLocaleString("en-US", {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            ))}
          </>
        )}

        {notifications.length > 0 && (
          <>
            <Divider />
            <Box sx={{ p: 1.5, textAlign: "center" }}>
              <Button
                fullWidth
                onClick={handleViewAll}
                sx={(theme) => ({
                  color:
                    theme.palette.mode === "dark"
                      ? theme.palette.common.white
                      : theme.palette.primary.main,
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  border: `1px solid ${theme.palette.primary.main}`,
                  borderRadius: 2,
                  py: 1,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.common.white,
                  },
                })}
              >
                View All Notifications
              </Button>
            </Box>
          </>
        )}
      </Menu>
    </>
  );
};

export default NotificationBell;
